import { Button, VariantBadge } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback } from "react";
import PdfRender from "../../../components/pdfRender/pdfRender";
import DownloadIcon from "../../../svg/download-icon";
import FileAttachmentIcon from "../../../svg/file-attachment-icon";
import {
  DOCUMENTS_LABEL,
  DOWNLOAD_LABEL,
  INCORRECT_LETTER_LABEL,
} from "../utils/constants";

interface AuthorisationDocumentViewerProps {
  pdfUrl: string;
  documentFileName?: string;
  onDownloadClick?: () => void;
  /** Incorrect Letter is disabled when no handler is supplied. */
  onIncorrectLetterClick?: () => void;
  /** Renders the Documents action; it is disabled when no handler is supplied. */
  showDocuments?: boolean;
  onDocumentsClick?: () => void;
  isLoading?: boolean;
}

const AuthorisationDocumentViewer = ({
  pdfUrl,
  documentFileName = "Letter 1.pdf",
  onDownloadClick,
  onIncorrectLetterClick,
  showDocuments = false,
  onDocumentsClick,
  isLoading = false,
}: AuthorisationDocumentViewerProps) => {
  const handleDownloadClick = useCallback(() => {
    if (onDownloadClick) {
      onDownloadClick();
      return;
    }
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
    }
  }, [onDownloadClick, pdfUrl]);

  const canDownload = Boolean(pdfUrl) && !isLoading;

  return (
    <div className="flex w-[40%] flex-col border-r border-primaryGray-15 bg-primaryGray-14/30">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[#E6E6E6] bg-[#F5F5F5] px-4 py-3">
        <span
          className="min-w-0 truncate text-sm font-bold"
          title={documentFileName}
        >
          {documentFileName}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onIncorrectLetterClick}
            disabled={!onIncorrectLetterClick}
            className="cursor-pointer rounded focus:outline-none focus:ring-2 focus:ring-primaryGray-15 disabled:cursor-not-allowed"
            aria-label={INCORRECT_LETTER_LABEL}
          >
            <VariantBadge
              text={INCORRECT_LETTER_LABEL}
              backgroundColor="#FFF"
              borderColor="#CC0300"
              textColor="#CC0300"
            />
          </button>
          <div className="flex flex-shrink-0 items-center gap-1.5 rounded-md border border-primaryGray-15 bg-[#FFF]">
            <Button
              buttonType="tertiary"
              size="small"
              onClick={handleDownloadClick}
              className="flex items-center gap-1.5"
              disabled={!canDownload}
            >
              <DownloadIcon width={14} height={14} color="#0F0F0F" />
              <span className="text-xs font-semibold text-[#0F0F0F]">
                {DOWNLOAD_LABEL}
              </span>
            </Button>
          </div>
          {showDocuments && (
            <div className="flex flex-shrink-0 items-center gap-1.5 rounded-md border border-primaryGray-15 bg-[#FFF]">
              <Button
                buttonType="tertiary"
                size="small"
                onClick={onDocumentsClick ?? (() => undefined)}
                className="flex items-center gap-1.5"
                disabled={!onDocumentsClick}
              >
                <FileAttachmentIcon />
                <span className="text-xs font-semibold text-[#0F0F0F]">
                  {DOCUMENTS_LABEL}
                </span>
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4">
        {isLoading ? (
          <div className="flex h-full min-h-[400px] items-center justify-center rounded-lg border border-dashed border-primaryGray-16 bg-white text-sm text-primaryGray-10">
            Loading document...
          </div>
        ) : pdfUrl ? (
          <PdfRender file={pdfUrl} />
        ) : (
          <div className="flex h-full min-h-[400px] items-center justify-center rounded-lg border border-dashed border-primaryGray-16 bg-white text-sm text-primaryGray-10">
            No document loaded
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthorisationDocumentViewer;
